import React, { useState } from 'react';
import binIcon from '@/assets/icons/bin.svg';
import budgetIcon from '@/assets/icons/budget.svg';
import editIcon from '@/assets/icons/edit.svg';
import infoIcon from '@/assets/icons/info.svg';
import { getCurrencyInfo } from '@/utils/helpers';
import { formatBudgetCalculationAmount } from '@/utils/budgetCalculations';

interface BudgetInfoCardProps {
  name?: string;
  type?: string;
  description?: string;
  currency?: string;
  btev?: number;
  bmev?: number;
  bhov?: number;
  btoh?: number;
  bmevThreshold?: number;
  btrt?: number;
  btrtThreshold?: number;
  startDate?: string;
  endDate?: string;
  isAutoRenewal?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
  onInfo?: () => void;
}

interface InfoFieldProps {
  label: string;
  value: React.ReactNode;
}

const InfoField: React.FC<InfoFieldProps> = ({ label, value }) => (
  <div>
    <p className="text-sm text-secondary font-medium leading-[18px]">{label}</p>
    <p className="text-base font-medium leading-[24.5px]">{value}</p>
  </div>
);

const BudgetInfoCard: React.FC<BudgetInfoCardProps> = ({
  name = 'BUDGET NAME',
  type = 'Company',
  description = '',
  currency = 'EUR',
  btev = 0,
  bmev = 100000,
  bhov = 0,
  btoh = 0,
  bmevThreshold = 80,
  btrt = 100000,
  btrtThreshold = 80,
  startDate = '01/01/2024',
  endDate = '12/31/2024',
  isAutoRenewal = true,
  onEdit,
  onDelete,
  onInfo
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="hidden flex-1 md:flex p-5 border xl:w-[60%] border-lightGray rounded-[16px] bg-white flex-col gap-[1.3rem]">
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src={budgetIcon} alt="budget-icon" width={40} height={40} />
          <div>
            <h4 className="font-semibold text-base md:text-[24px] md:mb-[0.3rem]">{name}</h4>
            <p className="text-secondary text-sm font-medium md:text-[18px]">{type}</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {onEdit && (
            <button type="button" onClick={onEdit} title="Edit">
              <img src={editIcon} alt="edit-icon" width={28} height={28} />
            </button>
          )}
          <button type="button" onClick={onInfo} title="Info">
            <img src={infoIcon} alt="edit-icon" width={28} height={28} />
          </button>
          {onDelete && (
            <button type="button" onClick={onDelete} title="Delete">
              <img src={binIcon} alt="bin-icon" width={28} height={28} />
            </button>
          )}
        </div>
      </header>
      <div className="border border-primary rounded-[16px] p-4 bg-lightPurple flex-1 space-y-3">
        <p className="text-primary font-bold text-base mb-3">General Information</p>

        <div>
          <p className="text-sm text-secondary font-medium leading-[18px]">Description</p>
          <p className={`text-base font-medium leading-[24.5px] ${isExpanded ? '' : 'line-clamp-2'}`}>
            {description || 'No description provided'}
          </p>
          {!isExpanded && description && description.length > 40 && (
            <button className="text-sm text-primary" onClick={() => setIsExpanded(true)}>
              See More
            </button>
          )}
          {isExpanded && (
            <button className="text-sm text-primary" onClick={() => setIsExpanded(false)}>
              See Less
            </button>
          )}
        </div>

        <div className="flex gap-4">
          <div className="flex-1 space-y-3">
            <InfoField
              label="Budget Total Expense Value (BTEV)"
              value={formatBudgetCalculationAmount(btev, currency)}
            />
            <InfoField
              label="Budget maximum expense value (BMEV)"
              value={formatBudgetCalculationAmount(bmev, currency)}
            />
            <InfoField
              label="Budget total revenue target (BTRT)"
              value={formatBudgetCalculationAmount(btrt, currency)}
            />
            <InfoField
              label="Budget Hours Operative Value (BHOV)"
              value={formatBudgetCalculationAmount(bhov, currency)}
            />
            <InfoField label="Budget Total Ore Hours (BTOH)" value={`${btoh.toLocaleString()} Hours`} />
          </div>

          <div className="flex-1 space-y-3">
            <InfoField label="Default Currency Type" value={getCurrencyInfo(currency)} />
            <InfoField label="Alerting threshold of BMEV" value={`${bmevThreshold}%`} />
            <InfoField label="Alerting threshold of BTRT" value={`${btrtThreshold}%`} />
            <InfoField label="Duration" value={`${startDate} - ${endDate}`} />
            <InfoField label="Automatic Renewal" value={isAutoRenewal ? 'On' : 'Off'} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BudgetInfoCard;
